/**
 * Chart spec produced by the Ask AI backend's built-in `create_chart` tool
 * (SPBFinnLambda chart_builder.py) and stored in the hidden CHARTS marker.
 *
 * KEEP-IN-SYNC: chart_builder.build_chart_spec / CHART_SPEC_VERSION.
 */

export type ChartType = "bar" | "line" | "area" | "combo";
export type ValueFormat =
  | "number"
  | "abbrev"
  | "percent"
  | "currency"
  | "decimal";

export interface ChartSeries {
  /** Safe data key ("s0", "s1", ...) used in every data point. */
  key: string;
  label: string;
  /** primary = main chart; secondary = the y2 panel under it (never a 2nd y-axis). */
  axis: "primary" | "secondary";
  mark: "bar" | "line" | "area";
  /** series_by value the series was split from, when any. */
  group?: string;
}

export interface ChartAxis {
  label: string;
  format: ValueFormat;
}

export interface ChartSpec {
  version: number;
  chart_id: string;
  type: ChartType;
  title: string;
  x: { label: string };
  axes: { primary: ChartAxis; secondary?: ChartAxis };
  series: ChartSeries[];
  /** One point per x value: { x: "2026-10-10", s0: 123, s1: null, ... }. */
  data: Array<{ x: string } & Record<string, number | string | null>>;
  source?: { result_id?: string; inline?: boolean };
}

/** Highest spec version this frontend renders; newer specs are skipped. */
export const SUPPORTED_CHART_SPEC_VERSION = 1;

/** Keep only well-formed specs this frontend can render. */
export function validChartSpecs(value: unknown): ChartSpec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (spec): spec is ChartSpec =>
      !!spec &&
      typeof spec === "object" &&
      typeof spec.version === "number" &&
      spec.version <= SUPPORTED_CHART_SPEC_VERSION &&
      Array.isArray(spec.series) &&
      spec.series.length > 0 &&
      Array.isArray(spec.data) &&
      spec.data.length > 0 &&
      !!spec.axes?.primary
  );
}
